import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export const TAB_LABEL_SHOW_MS = 2000;
const DISMISS_LISTENER_DELAY_MS = 300;
const DISMISS_GUARD_MS = 400;

const TAB_BAR_SELECTOR =
  ".patient-menu-scroll, .ip-compact-patient-tabs, .mobile-tab-scroll, .patientMenu, .mobile-tab-item-wrap, .mobile-tab-item";

const LABEL_ESTIMATE_HEIGHT = 32;
const VIEWPORT_EDGE_PAD = 8;

const clampTabTooltipX = (centerX, title) => {
  if (typeof window === "undefined") return centerX;
  const edgePad = 8;
  const estimatedWidth = Math.min(
    String(title).length * 7.2 + 20,
    window.innerWidth - edgePad * 2
  );
  const half = estimatedWidth / 2;
  const min = half + edgePad;
  const max = window.innerWidth - half - edgePad;
  return Math.min(Math.max(centerX, min), Math.max(min, max));
};

export const resolveTabAnchor = (domEventOrEl) => {
  if (domEventOrEl?.getBoundingClientRect) return domEventOrEl;

  const target = domEventOrEl?.target;
  if (!target) return domEventOrEl?.currentTarget || null;

  const menuItem = target.closest?.(".ant-menu-item");
  if (menuItem) return menuItem;

  return (
    target.closest?.(
      ".ant-menu-submenu-title, .mobile-tab-item, .mobile-tab-item-wrap"
    ) ||
    domEventOrEl?.currentTarget ||
    null
  );
};

/**
 * Tap-to-show tab helper label for compact + tablet layouts.
 * Desktop should use Ant Design Tooltip with trigger={['hover']} instead.
 */
const useTabHelperLabel = (enabled) => {
  const [tabTooltip, setTabTooltip] = useState(null);
  const tooltipDismissRef = useRef(null);
  const dismissListenerRef = useRef(null);
  const tabTooltipRef = useRef(null);
  const suppressDismissUntilRef = useRef(0);

  const dismissTabLabel = useCallback(() => {
    if (tooltipDismissRef.current) {
      window.clearTimeout(tooltipDismissRef.current);
      tooltipDismissRef.current = null;
    }
    if (dismissListenerRef.current) {
      document.removeEventListener(
        "pointerdown",
        dismissListenerRef.current,
        true
      );
      dismissListenerRef.current = null;
    }
    setTabTooltip(null);
  }, []);

  const showTabLabel = useCallback(
    (key, title, anchorEl) => {
      if (!enabled) return;
      if (!title || !anchorEl?.getBoundingClientRect) return;

      if (tooltipDismissRef.current) {
        window.clearTimeout(tooltipDismissRef.current);
      }
      if (dismissListenerRef.current) {
        document.removeEventListener(
          "pointerdown",
          dismissListenerRef.current,
          true
        );
        dismissListenerRef.current = null;
      }

      const rect = anchorEl.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const spaceAbove = rect.top - VIEWPORT_EDGE_PAD;
      const preferAbove = spaceAbove >= LABEL_ESTIMATE_HEIGHT + 12;

      setTabTooltip({
        key,
        title,
        x: clampTabTooltipX(centerX, title),
        y: preferAbove ? rect.top : rect.bottom,
        placement: preferAbove ? "above" : "below",
      });

      suppressDismissUntilRef.current = Date.now() + DISMISS_GUARD_MS;

      tooltipDismissRef.current = window.setTimeout(() => {
        setTabTooltip((current) => (current?.key === key ? null : current));
        tooltipDismissRef.current = null;
      }, TAB_LABEL_SHOW_MS);
    },
    [enabled]
  );

  useLayoutEffect(() => {
    const el = tabTooltipRef.current;
    if (!el || !tabTooltip) return;
    const width = el.getBoundingClientRect().width;
    const edgePad = 8;
    const half = width / 2;
    const min = half + edgePad;
    const max = window.innerWidth - half - edgePad;
    const clamped = Math.min(Math.max(tabTooltip.x, min), Math.max(min, max));
    if (clamped !== tabTooltip.x) {
      el.style.left = `${clamped}px`;
    }
  }, [tabTooltip]);

  useEffect(() => {
    if (!tabTooltip || !enabled) return undefined;

    const attachTimer = window.setTimeout(() => {
      const onPointerDown = (e) => {
        if (Date.now() < suppressDismissUntilRef.current) return;
        if (e.target.closest?.(TAB_BAR_SELECTOR)) return;
        if (tabTooltipRef.current?.contains(e.target)) return;
        dismissTabLabel();
      };
      dismissListenerRef.current = onPointerDown;
      document.addEventListener("pointerdown", onPointerDown, true);
    }, DISMISS_LISTENER_DELAY_MS);

    return () => {
      window.clearTimeout(attachTimer);
      if (dismissListenerRef.current) {
        document.removeEventListener(
          "pointerdown",
          dismissListenerRef.current,
          true
        );
        dismissListenerRef.current = null;
      }
    };
  }, [tabTooltip, enabled, dismissTabLabel]);

  useEffect(
    () => () => {
      if (tooltipDismissRef.current) {
        window.clearTimeout(tooltipDismissRef.current);
      }
      if (dismissListenerRef.current) {
        document.removeEventListener(
          "pointerdown",
          dismissListenerRef.current,
          true
        );
      }
    },
    []
  );

  const TabHelperLabel =
    enabled && tabTooltip
      ? createPortal(
          <div
            ref={tabTooltipRef}
            className={`mobile-tab-item-label mobile-tab-item-label--fixed mobile-tab-item-label--${tabTooltip.placement}`}
            style={{
              left: tabTooltip.x,
              top: tabTooltip.placement === "above" ? tabTooltip.y - 6 : tabTooltip.y + 6,
            }}
            role="status"
            aria-live="polite"
          >
            {tabTooltip.title}
          </div>,
          document.body
        )
      : null;

  return { showTabLabel, dismissTabLabel, TabHelperLabel };
};

export default useTabHelperLabel;
